import { ChangeEvent, FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { TextArea } from '@dynamika-shared/ui/TextArea/TextArea';
import { EyeCloseIcon, EyeIcon } from '@dynamika-shared/ui/icons';
import { Divider } from 'antd';
import {
  MAX_SIZE,
  useAttachments,
} from '@components/Queries/hooks/useAttachments';
import { AttachFileButton } from '@components/Queries/QueryPage/components/AttachFileButton';
import { SelectedQueryItems } from '@components/Queries/QueryPage/components/SelectedQueryItems';

type FormValues = {
  username: string | null | undefined;
  password: string | null | undefined;
  signAgreement: boolean | null | undefined;
  message: string;
  attachments: File[];
};

type QueryActionModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  okText?: string;
  textareaLabel?: string;
  textareaPlaceholder?: string;
  messageRequiredMsg?: string;
  enableAttachments?: boolean;
  subtitle?: string;
  onSubmitData: (data: FormValues) => void;
  updating: boolean;
  credentialsRequired?: boolean;
};

const formChangeOptions = { shouldDirty: true, shouldValidate: true };

export const QueryActionModal: FC<QueryActionModalProps> = ({
  open,
  onClose,
  title,
  okText,
  textareaLabel,
  textareaPlaceholder = 'Enter your reason',
  messageRequiredMsg,
  enableAttachments = false,
  subtitle,
  onSubmitData,
  updating,
  credentialsRequired = true,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const defaultValues = {
    message: '',
    attachments: [] as File[],
    ...(credentialsRequired && {
      username: '',
      password: '',
      signAgreement: false,
    }),
  };

  const schema: yup.ObjectSchema<FormValues> = yup.object({
    message: yup.string().required(messageRequiredMsg || 'Message is required'),
    attachments: yup
      .array()
      .of(yup.mixed<File>().required())
      .max(3, 'Maximum 3 attachments allowed')
      .test('maxSize', 'File size must not exceed 5 MB', files =>
        (files ?? []).every(file => file.size <= MAX_SIZE)
      )
      .test('unsupportedFormats', (files, ctx) => {
        const unsupported = (files ?? []).filter(file => !validateFile(file));
        if (unsupported.length === 0) {
          return true;
        }
        return ctx.createError({
          message: 'Unsupported file format. Only .jpg, .jpeg, .png, .pdf, .doc, .docx files are allowed',
        });
      })
      .required(),
    username: credentialsRequired
      ? yup
          .string()
          .required('Email is a required field')
          .email('Must be a valid email')
      : yup.string().notRequired(),
    password: credentialsRequired
      ? yup.string().required('Password is a required field')
      : yup.string().notRequired(),
    signAgreement: credentialsRequired
      ? yup
          .boolean()
          .oneOf([true], 'You must agree to the terms and conditions')
      : yup.boolean().notRequired(),
  });

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues,
  });
  const {
    watch,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    setValue,
    reset,
    trigger,
  } = methods;

  const formValues = watch();

  const { onAttachmentsChange, attachments, onRemoveAttachment, validateFile } =
    useAttachments<FormValues>({
      setValue,
      watch,
      field: 'attachments',
    });

  const onChange = (
    field: keyof FormValues,
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValue(field, e.target.value, formChangeOptions);
    trigger(field);
  };

  const handlePasswordView = () => {
    setShowPassword(currState => !currState);
  };

  const onOk = () => {
    handleSubmit(data => {
      onSubmitData({
        ...data,
        ...(!credentialsRequired && { signAgreement: true }),
      });
    })();
    reset();
    onClose();
  };

  const onCancel = () => {
    reset();
    onClose();
  };
  return (
    <Modal
      width={enableAttachments ? 632 : 464}
      onOk={onOk}
      onCancel={onCancel}
      okProps={{
        htmlType: 'submit',
        disabled: !isValid || updating || !isDirty,
        loading: updating,
      }}
      cancelClassName={'w-[18rem] text-black'}
      okClassName={'w-[18rem]'}
      okText={okText}
      maskClosable={false}
      open={open}
      title={title}
      titleClassName='text-left'
      subtitle={subtitle}
    >
      <form className='space-y-4'>
        <TextArea
          required
          label={textareaLabel}
          placeholder={textareaPlaceholder}
          name='message'
          value={formValues.message ?? ''}
          onChange={e => onChange('message', e)}
          errors={errors.message?.message}
          classNameInputWrapper='!max-w-full'
          className='!resize-none'
          rows={4}
        />

        {enableAttachments && (
          <>
            <div className='flex justify-between items-center'>
              <div className='w-full'>
                <h3 className='text-chineseBlack text-1420 font-semibold'>
                  Attachments
                </h3>
                <span className='text-dimGray text-1216 font-medium'>
                  Only .jpg, .png, .pdf, .doc and .docx formats are supported.{' '}
                  <br />
                  File must not exceed 5 MB
                </span>
                <SelectedQueryItems
                  selectedFiles={attachments as File[]}
                  onCloseFile={onRemoveAttachment}
                  className='flex-row flex-wrap gap-2 w-full'
                  getExtraFileTagClassName={file =>
                    file.size > MAX_SIZE || !validateFile(file)
                      ? 'bg-redOrange/10'
                      : ''
                  }
                />
                <AttachFileButton onChange={onAttachmentsChange}>
                  Attach File
                </AttachFileButton>
                {errors.attachments?.message && (
                  <div className='text-redOrange text-1420 font-normal mt-1'>
                    {errors.attachments.message}
                  </div>
                )}
              </div>
            </div>
            <Divider className='bg-brightGray' />
          </>
        )}

        {credentialsRequired && (
          <>
            <div className='flex gap-4'>
              <Input
                required
                label={'Email'}
                placeholder={'Enter your email'}
                name='username'
                value={formValues.username ?? ''}
                onChange={e => onChange('username', e)}
                errors={errors.username?.message}
                autoComplete='off'
                onPaste={e => e.preventDefault()}
                className='flex-1'
              />
              <Input
                required
                label='Password'
                type={showPassword ? 'text' : 'password'}
                name='password'
                value={formValues.password ?? ''}
                onChange={e => onChange('password', e)}
                placeholder='Enter password'
                autoComplete='off'
                onPaste={e => e.preventDefault()}
                suffix={
                  !showPassword ? (
                    <EyeCloseIcon
                      onClick={handlePasswordView}
                      className='w-[3.4rem] h-[3.2rem] hover:cursor-pointer text-spanishGray'
                    />
                  ) : (
                    <EyeIcon
                      onClick={handlePasswordView}
                      className='w-[3.4rem] h-[3.2rem] hover:cursor-pointer'
                    />
                  )
                }
                errors={errors.password?.message}
                className='flex-1 h-6'
              />
            </div>
            <Checkbox
              checked={formValues.signAgreement ?? false}
              onChange={e => {
                setValue('signAgreement', e.target.checked, formChangeOptions);
                trigger('signAgreement');
              }}
            >
              I confirm the information entered is accurate and electronically
              sign this action.
            </Checkbox>
          </>
        )}
      </form>
    </Modal>
  );
};
